// Canvas 2: Journey and Trust Map (workbook tab 02 Journey).
// The printed canvas shares six stage rows across three zones. Here each step has its own table over the same row ids r1 to r6:
// 'stages' (Step 1), 'trust' (Step 2) and 'ai' (Step 3). The trust and ai tables take their rows from the stages table.
// The trust moment is derived: any tick. Step 4 is the 'cands' table (row ids r1 to r5), which Canvas 3 reuses.
import { makeDef, G, text, long, pick, multi, tag, calc, fields, table, pull, reference } from './base.js';
import { OPT } from '../spine.js';
import { has, S, T, listOf, gap } from '../helpers.js';

const STAGE_MAX = 6;
const STAGE_MIN = 4;
const CAND_MAX = 5;
const CAND_MIN = 3;

// ---------------------------------------------------------------- local helpers
const card = (up) => (up && up[0] && up[0].d && up[0].d.facts) || {};

// the ticked letters in the order S, D, R
const ticksOf = (s) => OPT.sdr.filter((t) => listOf(s).includes(t));

// the AI type as a word: 'writes', 'scores and writes', 'scores, writes and acts'
const typeText = (s) => {
  const list = OPT.aitype.filter((t) => listOf(s).includes(t));
  if (list.length < 3) return list.join(' and ');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
};

// how many stage rows are showing: at least four, up to six, as far as the learner has typed or revealed
const revealed = (v) => {
  let last = 0;
  for (let n = 1; n <= STAGE_MAX; n += 1) {
    const used = ['stage', 'pain', 'tag'].some((c) => has(v[`stages.r${n}.${c}`]));
    if (used) last = n;
  }
  const extra = parseInt((v && v['stages._n']) || '0', 10) || 0;
  return Math.min(STAGE_MAX, Math.max(STAGE_MIN, last, extra));
};

// the rows of the trust and ai tables: one per stage row, labelled with the stage name
const stageListRows = (v) =>
  Array.from({ length: revealed(v || {}) }, (_, i) => {
    const id = `r${i + 1}`;
    return {
      id,
      label: S(v, `stages.${id}.stage`) || `Stage ${i + 1}`,
      sub: S(v, `stages.${id}.pain`) || undefined,
    };
  });

// every non-empty stage row, with the three steps joined (what Canvases 3 to 7 read)
const readStages = (v) => {
  const out = [];
  for (let n = 1; n <= STAGE_MAX; n += 1) {
    const id = `r${n}`;
    const row = {
      id,
      n,
      stage: S(v, `stages.${id}.stage`),
      pain: S(v, `stages.${id}.pain`),
      tag: S(v, `stages.${id}.tag`),
      sdr: ticksOf(S(v, `trust.${id}.sdr`)),
      human: S(v, `trust.${id}.human`),
      opp: S(v, `ai.${id}.opp`),
      type: typeText(S(v, `ai.${id}.type`)),
    };
    row.trust = row.sdr.length > 0;
    const any = row.stage || row.pain || row.tag || row.sdr.length || row.human || row.opp || row.type;
    if (any) out.push(row);
  }
  return out;
};

const readCands = (v) => {
  const out = [];
  for (let n = 1; n <= CAND_MAX; n += 1) {
    const id = `r${n}`;
    const row = {
      id,
      n,
      name: S(v, `cands.${id}.name`),
      stage: S(v, `cands.${id}.stage`),
      type: typeText(S(v, `cands.${id}.type`)),
      idea: S(v, `cands.${id}.idea`) === 'Yes',
    };
    if (row.name || row.stage || row.type || row.idea) out.push(row);
  }
  return out;
};

// ---------------------------------------------------------------- NorthStar (master spec section 6)
const NORTHSTAR = {
  owner: 'None named',
  ...T('stages', [
    { stage: 'Apply', pain: 'Form asks for what the bank already holds', tag: 'E' },
    { stage: 'Verify identity', pain: 'Repeated uploads, no status shown', tag: 'E' },
    { stage: 'Get approved or declined', pain: 'Generic decline with no reason', tag: 'E' },
    { stage: 'First 30 days', pain: 'No contact; account not funded', tag: 'G' },
  ]),
  ...T('trust', [
    { human: 'None needed' },
    { sdr: 'S', human: 'Officer reviews exceptions' },
    { sdr: 'D,R', human: 'A trained service officer calls on declines' },
    { human: 'Follow-up call at day 14' },
  ]),
  ...T('ai', [
    { opp: "Pre-fill the form and read the customer's documents", type: 'writes' },
    { opp: 'Guided upload assistant; automatic identity verification', type: 'writes,acts' },
    { opp: 'Decline reason and review', type: 'scores,writes' },
    { opp: 'Day-14 activation trigger', type: 'scores' },
  ]),
  ...T('cands', [
    { name: 'Pre-fill and document reading', stage: 'Apply', type: 'writes' },
    { name: 'Guided upload assistant', stage: 'Verify identity', type: 'writes' },
    { name: 'Automatic identity verification', stage: 'Verify identity', type: 'acts' },
    { name: 'Decline reason and review', stage: 'Get approved or declined', type: 'scores,writes' },
    { name: 'Day-14 activation trigger', stage: 'First 30 days', type: 'scores', idea: 'Yes' },
  ]),
  'so.reviewer': 'IT and Data',
  'so.objection': "Which of these pain points are in a report, and which are the team's view?",
  'so.evidence': 'Stages 1 to 3 are tagged E until call reasons are pulled; stage 4 is G and on the Evidence Gaps list',
  'so.owner': 'Head of CX',
  'so.status': 'Answered',
};

export default makeDef(2, {
  short: 'Journey',
  purpose: 'Map the stages of your priority journey, find the trust moments where AI would touch the customer, and number up to five use case candidates.',
  howTo: [
    "Map four to six stages of your priority journey in the customer's words. Write one pain point for each, and tag it M, E or G.",
    'Run the trust test on each stage: tick S, D or R. One tick makes a trust moment, and a trust moment needs a named human role.',
    'Name the AI opportunity at each stage and its type: scores, writes or acts. Then number up to five candidates for Canvas 3.',
    'Everything calculated here travels on to Canvases 3 to 7 and 9.',
  ],
  results: [
    { id: 'stages', label: 'Stages mapped', rule: 'Count the stage rows.' },
    { id: 'trust', label: 'Trust moments', rule: 'Stages with any tick.' },
    { id: 'human', label: 'Trust moments with a named human role', rule: 'Of the trust moments, how many name a human role.' },
    { id: 'tagged', label: 'Pain points tagged M or E', rule: 'Of the pain points you wrote.' },
    { id: 'cands', label: 'Candidates', rule: 'Count Step 4.' },
    { id: 'owner', label: 'Journey owner today', rule: 'From the top line. None is a finding.' },
  ],
  steps: [
    {
      hint:
        "Name each stage in the customer's words: four are enough, and the canvas has room for six. " +
        'Write one pain point per stage, then tag it M, E or G.',
      blocks: [
        pull('p1', 'From your Case Card', [
          {
            id: 'journey',
            label: 'Journey',
            ref: 'Case Card B',
            no: 0,
            get: (up) => card(up).journey || null,
            note: 'Keep Case Card B beside you: your channels and where the journey breaks today.',
          },
          { id: 'channels', label: 'Channels', ref: 'Case Card B', no: 0, get: (up) => card(up).channels || null },
          {
            id: 'breaks',
            label: 'Where it breaks today',
            ref: 'Case Card B',
            no: 0,
            get: (up) => [card(up).break1, card(up).break2].filter(Boolean).join('\n') || null,
          },
        ]),
        fields([
          text('owner', 'Journey owner today (a role; a blank is a finding)', {
            w: 4,
            placeholder: 'A role, or leave blank if nobody owns the whole journey',
            guide: G(
              'Write the role that owns the whole journey today, from the first step to the outcome the customer wanted. Leave it blank if there is none.',
              'None named',
              'A blank is a finding. It shows as "None named: a finding" in the results.',
              'A head of one stage or one channel is not a journey owner.',
            ),
          }),
        ]),
        table('stages', {
          title: 'Stages and pain points',
          rows: { min: STAGE_MIN, max: STAGE_MAX, start: STAGE_MIN, label: 'Stage' },
          pdf: 'table',
          columns: [
            text('stage', "Stage, in the customer's words", {
              required: true,
              w: 2,
              placeholder: 'For example: Verify identity',
              guide: G(
                'Name the stage in the customer\'s words: what they are trying to get done at that point.',
                'Apply; Verify identity; Get approved or declined; First 30 days',
                'Four stages are enough. The canvas has room for six.',
                'A team or a system is not a stage. "Credit operations" is a department; "Get approved or declined" is a stage.',
              ),
            }),
            long('pain', 'Pain point: what the customer does, waits for or repeats', {
              required: true,
              w: 3,
              rows: 2,
              placeholder: 'What the customer does, waits for or repeats',
              guide: G(
                'Write one pain point for the stage: what the customer does, waits for or repeats.',
                'Form asks for what the bank already holds',
                undefined,
                'Write what the customer experiences, not what the bank intends. "Slow process" is vague; "Repeated uploads, no status shown" is a pain point.',
              ),
            }),
            tag('tag', 'Tag', {
              required: true,
              w: 1,
              guide: G(
                'M if a report or system shows the pain point; E if you estimated it and can state the basis; G if it is a guess.',
                'E for stages 1 to 3 (not yet in a report); G for stage 4',
                'A pain point tagged E or G goes to the Evidence Gaps list.',
                "Your team's view is not a measurement. Tag it E until a report backs it.",
              ),
            }),
          ],
        }),
      ],
    },
    {
      hint: [
        'Trust test: tick S if the customer shares sensitive data, D if the outcome decides something that matters to them,',
        'R if it would be hard to reverse. One tick makes a trust moment, and a trust moment needs a named human role.',
      ].join(' '),
      blocks: [
        table('trust', {
          title: 'Trust test',
          rowLabel: 'Stage',
          rows: { from: (up, v) => stageListRows(v) },
          pdf: 'table',
          columns: [
            multi('sdr', 'Trust test: S, D, R', OPT.sdr, {
              w: 2,
              guide: G(
                'Tick S if the customer shares sensitive data, D if the outcome decides something that matters to them, R if it would be hard to reverse.',
                'Verify identity: S. Get approved or declined: D and R. Apply and First 30 days: no tick.',
                'One tick makes a trust moment (AICX working rule). Canvas 5 picks the riskiest trust moment by the number of ticks.',
                'No tick is a legitimate answer. Do not tick all three everywhere: it hides the stage that really is the riskiest.',
              ),
            }),
            calc('moment', 'Trust moment?', {
              w: 1,
              rule: 'Yes if any tick',
              guide: G('Worked out for you: Yes when you have ticked S, D or R on the stage, No when you have not.', 'Yes for Verify identity and for Get approved or declined.'),
            }),
            text('human', 'Human role (required at a trust moment)', {
              w: 3,
              required: ({ row }) => listOf(row.sdr).length > 0,
              placeholder: 'The role that steps in',
              guide: G(
                'Name the human role that steps in at this stage. Required wherever you ticked S, D or R. Elsewhere, write who follows up, or "None needed".',
                'A trained service officer calls on declines',
                'A trust moment needs a named human role.',
                'Name a role that exists: "a trained service officer", not "a human" or "the team".',
              ),
            }),
          ],
        }),
        reference('The trust test: S, D and R (an AICX working rule)', ['Tick', 'Ask at this stage', 'Illustrative example'], [
          ['S', 'Does the customer share sensitive data?', 'Verify identity'],
          ['D', 'Does the outcome decide something that matters to them?', 'Get approved or declined'],
          ['R', 'Would the outcome be hard to reverse?', 'Get approved or declined'],
        ]),
      ],
    },
    {
      hint: [
        'Write the opportunity verb first. Its type, in a word: scores (ranks, flags or forecasts), writes (creates new content on request),',
        'acts (plans its own steps, then takes them). Tick more than one if it does more than one.',
        'Leave it blank where AI is not the answer.',
      ].join(' '),
      blocks: [
        table('ai', {
          title: 'AI opportunity and type',
          rowLabel: 'Stage',
          rows: { from: (up, v) => stageListRows(v) },
          pdf: 'table',
          columns: [
            long('opp', 'AI opportunity (verb first)', {
              w: 3,
              rows: 2,
              placeholder: 'For example: Pre-fill the form',
              guide: G(
                'Write what AI would do at this stage, verb first. Leave it blank where AI is not the answer.',
                "Pre-fill the form and read the customer's documents",
                undefined,
                'Start with a verb: "Pre-fill", "Explain", "Trigger". A technology name is not an opportunity.',
              ),
            }),
            multi('type', 'Type', OPT.aitype, {
              w: 3,
              required: ({ row }) => has(row.opp),
              guide: G(
                'Tick what the AI does. Scores ranks, flags or forecasts. Writes creates new content on request. Acts plans its own steps, then takes them.',
                'writes; writes and acts; scores and writes; scores',
                'Tick more than one if it does more than one: "writes and acts".',
                'Do not tick acts because it sounds advanced. Tick it only if the AI plans its own steps and takes them.',
              ),
            }),
          ],
        }),
      ],
    },
    {
      hint: [
        'Number up to five candidates for Canvas 3: a short name, the stage and the type for each.',
        'Tick the one that carries your Case Card idea (E). If it is missing, add it.',
      ].join(' '),
      blocks: [
        pull('p4', 'From your Case Card', [
          {
            id: 'idea',
            label: 'The idea on the Case Card',
            ref: 'Case Card E',
            no: 0,
            get: (up) => card(up).idea || null,
            note: 'Tick the candidate that carries this idea.',
          },
        ]),
        table('cands', {
          title: 'Candidates for Canvas 3',
          rows: { min: CAND_MIN, max: CAND_MAX, start: CAND_MAX, label: 'Candidate' },
          pdf: 'table',
          columns: [
            text('name', 'Use case candidate (a short name)', {
              required: true,
              w: 2,
              placeholder: 'A short name',
              guide: G(
                'Give each candidate a short name a colleague would recognise. Draw them from the AI opportunities in Step 3, and add your Case Card idea.',
                'Pre-fill and document reading',
                'Number up to five. The name travels with the use case to Canvases 3 to 9.',
                'Keep it short. A long description will not fit on the later canvases.',
              ),
            }),
            text('stage', 'Stage', {
              required: true,
              w: 2,
              placeholder: 'A stage from Step 1',
              guide: G(
                'Type the stage from Step 1 where this candidate acts.',
                'Apply',
                undefined,
                'Use the stage name exactly as in Step 1, so Canvas 3 and Canvas 5 can match them.',
              ),
            }),
            multi('type', 'Type', OPT.aitype, {
              required: true,
              w: 2,
              guide: G(
                'Tick what the candidate does: scores, writes, acts, or more than one.',
                'writes; acts; scores and writes; scores',
                'Scores ranks, flags or forecasts. Writes creates new content on request. Acts plans its own steps, then takes them.',
                'Tick acts only if the candidate plans its own steps and takes them.',
              ),
            }),
            pick('idea', 'Case Card idea?', OPT.yes, {
              w: 1,
              guide: G(
                'Tick the one candidate that carries the AI idea you wrote on Case Card section E. If it is missing, add it as a candidate.',
                'Yes, for Day-14 activation trigger (it reuses the churn model on the Case Card).',
                'Canvas 3 shows this idea beside the candidate that carries it.',
                'Do not leave your own Case Card idea out because it scored badly in your head. Let Canvas 3 score it.',
              ),
            }),
          ],
        }),
      ],
    },
  ],
  derive(v, up) {
    const stages = readStages(v);
    const cands = readCands(v);
    const owner = S(v, 'owner');
    const named = stages.filter((r) => r.stage);
    const trustRows = named.filter((r) => r.trust);
    const withHuman = trustRows.filter((r) => r.human);
    const pains = stages.filter((r) => r.pain);
    const tagged = pains.filter((r) => r.tag === 'M' || r.tag === 'E');
    const candNames = cands.filter((c) => c.name);

    const calcOut = {};
    for (let n = 1; n <= STAGE_MAX; n += 1) {
      const row = stages.find((r) => r.n === n);
      calcOut[`trust.r${n}.moment`] = row && row.stage ? (row.trust ? 'Yes' : 'No') : '';
    }

    const some = named.length > 0;
    const results = {
      stages: some ? `${named.length} of ${STAGE_MAX}` : '',
      trust: some ? String(trustRows.length) : '',
      human: some ? `${withHuman.length} of ${trustRows.length}` : '',
      tagged: some ? `${tagged.length} of ${pains.length}` : '',
      cands: candNames.length ? `${candNames.length} of ${CAND_MAX}` : '',
      owner: owner || (some ? 'None named: a finding' : ''),
    };

    const evidence = [];
    stages.forEach((r) => {
      if ((r.stage || r.pain) && (r.tag === 'E' || r.tag === 'G')) {
        evidence.push(gap(`${r.stage || `Stage ${r.n}`}: ${r.pain}`, r.tag, `Canvas 2, pain point, stage ${r.n}`));
      }
    });

    const warnings = [];
    if (some && trustRows.length === 0) {
      warnings.push({ step: 's2', text: 'No trust moment yet. Check each stage against S, D and R: one tick makes a trust moment.' });
    }
    const idea = card(up).idea || '';
    if (has(idea) && !/^none/i.test(idea) && candNames.length > 0 && !cands.some((c) => c.idea)) {
      warnings.push({ step: 's4', text: 'Mark the candidate that carries the Case Card idea, or add it as a candidate.' });
    }
    const stageNames = named.map((r) => r.stage.toLowerCase());
    cands.forEach((c) => {
      if (c.stage && stageNames.length && !stageNames.includes(c.stage.toLowerCase())) {
        warnings.push({ step: 's4', text: `Candidate ${c.n}: the stage is not one of the stages you named in Step 1.` });
      }
    });

    return {
      calc: calcOut,
      results,
      evidence,
      warnings,
      blockers: [],
      facts: {
        journey: card(up).journey || '',
        owner,
        stages,
        candidates: cands,
      },
    };
  },
  NORTHSTAR,
});
